import { disablePonty, enablePonty, PontyService, adminAddPonty, adminDeletePonty } from '@/functions/services-functions';
import { confirmDialogue, generalError, inputDialogue } from '@/functions/system-modal-functions';
import { i18n } from '@/utilities/vue-i18n';
import { defineComponent, PropType, reactive, watch } from '@vue/composition-api';
import { ModalIconType } from '../system-modals/system-modals';
import { ConnectionStatus } from '@/views/connections/connections-types';

type SettingsType = {
  connection: PontyService;
  loading: boolean;
};

const validateFeedUrl = (input: string) => {
  if (!input) return false;

  try {
    const url = new URL(input);

    if (!url) return false;

    if (url.protocol !== 'https:') return false;

    if (!url.hostname.endsWith('ponty-system.se')) return false;

    if (!url.pathname.includes('/extapi/job')) return false;

    const param = url.searchParams.get('p');

    if (!param) return false;

    if (param.trim() === '') return false;

    return true;
  } catch
  {
    return false;
  }

};

export default defineComponent({
  props: {
    connection: {
      required: true,
      type: Object as PropType<PontyService>,
    },
    companyId: {
      required: true,
      type: Number,
    },
    allowDelete: {
      required: false,
      type: Boolean,
      default: false,
    },
    isAdmin: {
      required: false,
      type: Boolean,
      default: false,
    },
    loading: {
      required: false,
      type: Boolean,
      default: false,
    }
  },
  setup(props, context) {

    const settings = reactive<SettingsType>({
      connection: { ...props.connection },
      loading: props.loading,
    });

    watch(() => props.connection, (value) => settings.connection = { ...value }, { deep: true });
    watch(() => props.loading, (value) => settings.loading = value ?? settings.loading);

    const enable = async () => {
      if (settings.loading) {
        return;
      }

      settings.loading = true;

      const dialogue = await inputDialogue({
        title: 'Ponty',
        message: i18n.t('components.connection-ponty.add-ponty-feed-url'),
        validation: validateFeedUrl,
      });

      if (dialogue.confirmed) {
        const input = dialogue.input;

        const service = props.isAdmin ?
          await adminAddPonty(props.companyId, input) :
          await enablePonty(props.companyId, input);

        if (service) {
          settings.connection = service;

          context.emit('updated', settings.connection);
        }
        else {
          await generalError(false);
        }
      }

      settings.loading = false;
    };

    const disable = async () => {
      if ((!props.allowDelete && !props.isAdmin) || settings.loading) {
        return;
      }

      settings.loading = true;

      const confirmed = await confirmDialogue(
        'Ponty',
        i18n.t('components.connection-ponty.disable-ponty-service'),
        ModalIconType.warning
      );

      if (confirmed) {        
        if (props.isAdmin ? await adminDeletePonty(settings.connection.id) : await disablePonty(settings.connection.id)) {
          context.emit('updated', null);
        }
        else {
          await generalError(false);
        }
      }

      settings.loading = false;
    };

    return {
      settings,
      enable,
      disable,
      ConnectionStatus,
    };
  }
});